import React, { useCallback, useRef } from "react";
import { strings } from "../../resources/strings";

export interface SessionTimeoutWarningProps {
  /** Whether the warning is showing. */
  open: boolean;
  /** Whole seconds left before the session ends. */
  secondsRemaining: number;
  /** Keep the session — resets the idle timer. */
  onContinue: () => void;
}

/**
 * Warns before an idle timeout ends the session — WCAG 2.1 SC 2.2.1 Timing
 * Adjustable.
 *
 * The app previously signed a participant out after ten minutes of inactivity
 * with no warning and no way to continue. 2.2.1 requires at least one of: turn
 * the limit off, adjust it, or extend it. Extending is the only one of the
 * three that does not weaken the security posture on a shared kiosk, which is
 * where this app mostly runs.
 *
 * `role="alertdialog"` and the live countdown matter here: the warning appears
 * without focus moving to it, so nothing would otherwise announce it (4.1.3).
 * The countdown is `aria-live="polite"` rather than assertive — a value
 * changing every second on an assertive region would interrupt continuously.
 *
 * Focus IS contained, and that is not optional: `aria-modal="true"` asserts the
 * rest of the page is unavailable, and asserting it without making it true is
 * the exact defect this programme found on the navigation drawer. The trap here
 * is safe to hand-roll precisely where the drawer's was not — this dialog has
 * one focusable child by construction, so there is no variable child set to get
 * wrong, and Escape is deliberately NOT an exit: dismissing the warning without
 * choosing would let the session end silently, which is what 2.2.1 forbids.
 */
const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  open,
  secondsRemaining,
  onContinue,
}) => {
  const continueRef = useRef<HTMLButtonElement>(null);

  // Single focusable child, so containment is "keep focus here" rather than a
  // cycle. Shift+Tab included — otherwise focus escapes backwards into the
  // page behind, which aria-modal says is unavailable.
  const keepFocusInside = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== "Tab") return;
    e.preventDefault();
    continueRef.current?.focus();
  }, []);

  if (!open) return null;

  return (
    // The keydown handler is the Tab containment the docblock describes; an
    // alertdialog is the one non-interactive role where that belongs.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      data-testid="session-timeout-warning"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-timeout-title"
      aria-describedby="session-timeout-body"
      onKeyDown={keepFocusInside}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
        <h2
          id="session-timeout-title"
          className="text-xl font-semibold text-primary"
        >
          {strings.sessionTimeout.heading}
        </h2>
        <p id="session-timeout-body" className="mt-2 text-base text-[#515964]">
          {strings.sessionTimeout.body}
        </p>
        <p aria-live="polite" className="mt-1 text-base font-semibold">
          {`${secondsRemaining}`}
        </p>
        <button
          type="button"
          ref={continueRef}
          data-testid="session-timeout-continue"
          onClick={onContinue}
          // A dialog must move focus into itself when it opens; this is the
          // dialog's only control.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          className="mt-5 min-h-[44px] w-full rounded-2xl bg-primary font-semibold text-accent focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          {strings.sessionTimeout.continue}
        </button>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;
